import {
  Button,
  Command,
  CommandInput,
  CommandItem,
  CommandList,
  Input,
  Drawer,
  DrawerContent,
  DrawerTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  CommandEmpty,
} from '@e2n/ui';
import { cn } from '@/lib/utils';
import { faAnglesUpDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { HTMLAttributes, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

/* eslint-disable max-lines-per-function */
export function Select<T extends { id: string; name: string }>({
  onChange,
  options,
  selectedOption,
  drawerLabel,
  renderListItem,
  renderTriggerContent,
  disabled = false,
}: {
  onChange: (option: T) => void;
  drawerLabel: string;
  selectedOption?: T | undefined;
  options: T[];
  renderListItem: (option: T, selectedOption?: T) => ReactNode;
  renderTriggerContent?: (selectedOption: T) => ReactNode;
  disabled?: boolean;
}) {
  const { t } = useTranslation();

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [filter, setFilter] = useState('');

  function handleSelect(option: T) {
    onChange(option);
  }

  function onChangeFilter(value: string) {
    setFilter(value);
  }

  const filteredOptionsDrawer = options
    .filter((o) =>
      o.name
        .toLocaleLowerCase()
        .includes(filter.toLocaleLowerCase()),
    )

  return (
    <div>
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverTrigger className="hidden md:flex" asChild>
          <Trigger
            disabled={disabled}
            selectedOption={selectedOption}
            renderTriggerContent={renderTriggerContent}
          />
        </PopoverTrigger>
        <PopoverContent align="start" className={`bg-white p-2 px-0 w-108`}>
          <Command>
            <CommandInput
              placeholder={t('domain.mitarbeiter.rotationsplan.filter.suchen')}
            />
            <CommandList>
              <CommandEmpty className='text-neutrals-700 py-1'>{t('domain.mitarbeiter.rotationsplan.filter.keineErgebnisse')}</CommandEmpty>
              {options.map((option) => (
                <CommandItem
                  key={option.id}
                  value={option.name}
                  onSelect={() => {
                    handleSelect(option);
                    setIsPopoverOpen(false);
                  }}>
                  {renderListItem(option, selectedOption)}
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        <DrawerTrigger className="flex md:hidden" asChild>
          <Trigger
            disabled={disabled}
            selectedOption={selectedOption}
            renderTriggerContent={renderTriggerContent}
          />
        </DrawerTrigger>
        <DrawerContent className="flex flex-col gap-4 p-4">
          <div className="font-semibold text-center">{drawerLabel}</div>
          <Input
            className="grow"
            defaultValue={filter}
            placeholder={t('domain.mitarbeiter.rotationsplan.filter.suchen')}
            onChange={(event) => onChangeFilter(event.target.value)}
          />
          <ScrollArea className="overflow-auto">
            <div className="flex flex-col gap-2">
              {!filteredOptionsDrawer.length && <div className='text-neutrals-700 py-1 text-center text-sm'>{t('domain.mitarbeiter.rotationsplan.filter.keineErgebnisse')}</div>}
              {
                filteredOptionsDrawer.map((option, index) => (
                  <div
                    className="flex flex-col gap-2"
                    key={option.id}
                    onClick={() => {
                      handleSelect(option);
                      setIsDrawerOpen(false);
                    }}>
                    {renderListItem(option, selectedOption)}
                    {index < filteredOptionsDrawer.length - 1 && (
                      <div className="w-full bg-neutrals-200 h-px"></div>
                    )}
                  </div>
                ))}
            </div>
          </ScrollArea>
        </DrawerContent>
      </Drawer>
    </div>
  );
}

function Trigger<T extends { id: string; name: string }>({
  selectedOption,
  renderTriggerContent,
  disabled,
  ...props
}: {
  selectedOption: T;
  renderTriggerContent?: (selectedOption: T) => ReactNode;
  disabled: boolean;
} & HTMLAttributes<HTMLButtonElement>) {
  const { t } = useTranslation();
  return (
    <Button
      {...props}
      disabled={disabled}
      variant="outline-secondary"
      type="button"
      role="combobox"
      className={cn(
        'w-full justify-between font-normal  [&>svg]:text-neutrals-900',
        selectedOption ? 'text-neutrals-900' : 'text-neutrals-700',
        props.className,
      )}>
      {renderTriggerContent && selectedOption
        ? renderTriggerContent(selectedOption)
        : selectedOption
          ? selectedOption.name
          : t('actions.auswaehlen')}
      <FontAwesomeIcon icon={faAnglesUpDown} />
    </Button>
  );
}
